import { store } from '../../store';
import evBus from '../../utils/evbus';
import { styleToObj } from '../../components/modules/wheel/utils/index';
import {
  specialDateToString,
  getIsOver30Days,
  getTranslatedText,
  getCurrencyNumberInfo,
  getNumberInfo,
  getLanguageCode,
  extractAR,
  getRelativeTimeString,
  formatTo12HourTime,
} from './functions';
import { formatDate } from '@/components/modules/bets/utils/functions';
import prizeCurrencyFormat from '@/utils/prizeCurrencyFormat';

interface Props {
  [key: string]: any;
}
interface Context {
  [key: string]: any;
}

interface Returns extends Array<any | boolean | null> {
  /**
   * value
   *
   * @type {any|null}
   */
  0: any | null;
  /** appliedFromContext */
  1: boolean;
}

export type PagesSEORules = Array<{ path: string; text: unknown }>;

interface EvaluatePropFunction {
  (props: Props, context?: Context): boolean;
}

// const regExp = /<[^>]*>?/gm; // moved to functions.ts

const globalFN = {
  styleToObj,
  specialDateToString,
  formatTo12HourTime,
  getRelativeTimeString,
  getIsOver30Days,
  slugify: (str: string) => {
    return String(str)
      .normalize('NFKD') // split accented characters into their base characters and diacritical marks
      .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
      .trim() // trim leading or trailing whitespace
      .toLowerCase() // convert to lowercase
      .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
      .replace(/\s+/g, '-') // replace spaces with hyphens
      .replace(/-+/g, '-'); // remove consecutive hyphens
  },
  getTranslatedText,
  getLanguageCode,
  getCurrencyNumberInfo,
  getNumberInfo,
  getSEOFooterContent: (pages: PagesSEORules, pathName: string) => {
    const orderedRules = [...pages].sort((a, b) => {
      const pathA = a.path;
      const pathB = b.path;

      if (pathA === '*') {
        return 1;
      } else if (pathB === '*') {
        return -1;
      } else {
        const segmentsA = pathA.split('/').filter((s) => s !== '').length;
        const segmentsB = pathB.split('/').filter((s) => s !== '').length;

        if (segmentsA !== segmentsB) {
          return segmentsB - segmentsA;
        } else {
          return pathA.localeCompare(pathB);
        }
      }
    });

    const foundRule = orderedRules.find((rule) => {
      const isAsteriskPresent = rule.path.includes('*');
      const strippedPath = isAsteriskPresent ? rule.path.split('*')[0] : rule.path;
      // console.log('Rule to test: ', rule.path);
      // console.log('Path: ', pathName);
      return isAsteriskPresent ? pathName.includes(strippedPath) : pathName === strippedPath;
    });

    return foundRule;
  },
  formatMatchDate: (date: number) => {
    const appLang: string = store.getState().application.language ?? window.config.defaultLanguage;
    return formatDate(date, appLang);
  },
  prizeCurrencyFormat,
  extractAR,
  extractGamesFromAllGames: (games: any[string]) => {
    if (!Array.isArray(games)) {
      console.warn('globalFN.extractGamesFromAllGames: games is not an array', games);
      return [];
    }

    // This function extracts games from the all_slot_games_api data source using an array of game IDs.
    const allGames = store.getState().dataSources?.items?.[window.config.dataSourceAllGames]?.data || [];

    const gamesMap: { [key: string]: boolean } = {};
    for (const game of games) {
      if (typeof game === 'string') gamesMap[game] = true;
    }

    // console.log('extractGamesFromAllGames', { games, allGames, gamesMap });
    return allGames.filter((g: any) => gamesMap[g?.id]);
  },
  isEmptyRichText: (object: { [language: string]: string } | string) => {
    let text: any = object;
    if (text == null) return true;
    if (typeof text === 'string') {
      if (text.trim() === '') return true;
      try {
        text = JSON.parse(text);
      } catch (e) {}
    }
    if (text == null || typeof text !== 'object') return true;

    const values = Object.values(text);
    if (values.length === 0) return true;
    return values.every(
      (val) =>
        val == null ||
        (typeof val === 'string' && (val.trim() === '' || val.trim() === '<p></p>' || val.trim() === '<p><br></p>')),
    );
  },
};

// store pro
let functionsCache: any = {};

export const invalidatePropsFunctionsCache = () => {
  functionsCache = {};
};

export const evaluatePropFunction: EvaluatePropFunction = (props, context) => {
  const state = store.getState();
  const propFunction = props?.properties['propFunction'] != null ? props?.properties['propFunction'] : null;

  if (props != null && propFunction != null && propFunction) {
    try {
      let fn = null;
      if (props.uniqueId && functionsCache[props.uniqueId] == null) {
        fn = window.Function(`"use strict"; return ${propFunction}`)();
        //console.log('evaluatePropFunction[CacheMiss]', { uniqueId: props.uniqueId });
      } else if (
        props.uniqueId &&
        functionsCache[props.uniqueId] != null &&
        functionsCache[props.uniqueId].length === propFunction.length
      ) {
        fn = functionsCache[props.uniqueId].fn;
        //console.log('evaluatePropFunction[CacheHit]', { uniqueId: props.uniqueId });
      } else {
        fn = window.Function(`"use strict"; return ${propFunction}`)();
        //console.log('evaluatePropFunction[NoCache]', { uniqueId: props.uniqueId });
      }

      if (fn && props.uniqueId) {
        functionsCache[props.uniqueId] = {
          fn,
          length: propFunction.length,
        };
      }

      // this function might change the props values
      fn(props, context, state, evBus, globalFN);
    } catch (err) {
      try {
        console.log('evaluatePropFunction: function call', { err, props: JSON.parse(JSON.stringify(props)), context });
      } catch (e) {
        console.log('evaluatePropFunction: function call', { err, props, context });
      }
    }
    return true;
  }
  return false;
};
